// Validation constants for the FastAPI backend
export const WATCH_LATER_SIZES = ["Just a few (0-10)", "Moderate (11-50)", "Quite a lot (51-100)", "Too many (101+)"];
export const PREDEFINED_PLATFORMS = [
  'Instagram', 'TikTok', 'Twitter', 'Reddit', 'Podcasts', 'Blogs'
] as const;
export const PREDEFINED_USE_CASES = [
  "Work research", "Personal learning", "School study", "Content creation"
] as const;
export const PREDEFINED_ROLES = [
  "Student", "Professional", "Creator", "Researcher", 
] as const;

export type WatchLaterSize = typeof WATCH_LATER_SIZES[number];
export type PredefinedPlatform = typeof PREDEFINED_PLATFORMS[number];
export type PredefinedUseCase = typeof PREDEFINED_USE_CASES[number];
export type PredefinedRole = typeof PREDEFINED_ROLES[number];

export interface WaitlistFormData {
  email: string;
  keepUpdated: boolean;
  referralCode?: string; // Add referral code support
  whatWouldYouQuellect?: string;
  nextPlatforms?: string[];
  primaryUseCase?: string;
  role?: string;
  watchLaterSize?: string;
}

export interface WaitlistSubmission {
  email: string;
  keepUpdated: boolean;
  questions?: {
    whatWouldYouQuellect?: string;
    nextPlatforms?: string[];
    primaryUseCase?: string;
    role?: string;
    watchLaterSize?: string;
  };
  personaVariant: string;
  metadata: {
    timestamp: string;
    userAgent: string;
    referrer?: string;
  };
}

// Email Verification Types
export interface EmailVerificationRequest {
  token: string;
}

export interface EmailVerificationResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    verified: boolean;
    verifiedAt: string;
    referralCode: string;
    referralLink: string;
    referralStats: {
      maxUses: number | null;
      currentUses: number;
      remainingUses: string;
    };
  } | null;
  error?: string;
}

export interface VerificationStatsRequest {
  email: string;
}

export interface VerificationStatsResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    isVerified: boolean;
    verificationStatus: 'pending' | 'verified' | 'expired' | 'failed';
    referralCode?: string;
    referralLink?: string;
    referralStats?: {
      maxUses: number | null;
      currentUses: number;
      remainingUses: string;
    };
  } | null;
  error?: string;
}

// Enhanced Email Submission Types
export interface EmailSubmissionRequest {
  email: string;
  keepUpdated: boolean;
  referralCode?: string;
  metadata: {
    userAgent: string;
    referrer?: string;
    source?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  };
}

export interface EmailSubmissionResponse {
  success: boolean;
  message: string;
  data: {
    sessionId: string;
    email: string;
    keepUpdated: boolean;
    referralCode?: string;
    verificationRequired: boolean; // Add verification status
  } | null;
  error?: string;
}

export interface QuestionsSubmissionRequest {
  sessionId: string;
  answers: {
    watchLaterSize: WatchLaterSize;
    whatWouldYouQuellect: string;
    nextPlatforms: string[];
    primaryUseCase: string;
    role: string;
    otherThoughts?: string;
  };
  metadata: {
    userAgent: string;
    referrer?: string;
    source?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  };
}

// Referral code verification response
export interface ReferralCodeVerificationResponse {
  success: boolean;
  message: string;
  data: {
    referral_code: string;
    is_valid: boolean;
    is_active: boolean;
    current_uses: number;
    max_uses: number | null;
    is_auto_generated: boolean;
    generation_source: string;
    completion_status: string;
    has_submitted_questions: boolean;
    created_at: string;
    merged_at: string | null;
  } | null;
  error?: string;
}

// Email verification for questions access
export interface EmailVerificationForQuestionsRequest {
  email: string;
}

export interface EmailVerificationForQuestionsResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    is_subscribed: boolean;
    is_verified: boolean;
    subscription_status: 'registered' | 'verified' | 'completed';
    has_submitted_questions: boolean;
    referral_code: string | null;
    referral_code_status: string | null;
    created_at: string;
    verified_at: string | null;
    questions_submitted_at: string | null;
  } | null;
  error?: string;
}

export interface QuestionsSubmissionResponse {
  success: boolean;
  message: string;
  data: {
    sessionId: string;
    status: string;
    referralCode: string;
    referralLink: string;
  } | null;
  error?: string;
}

export interface HealthCheckResponse {
  success: boolean;
  message: string;
  data: {
    status: string;
    timestamp: string;
    version?: string;
  } | null;
  error?: string;
}

export interface WaitlistStatsResponse {
  success: boolean;
  message: string;
  data: {
    totalEmails: number;
    totalQuestions: number;
    activeReferralCodes: number;
    completedReferrals: number;
    completionRate: number;
  } | null;
  error?: string;
}

export interface WaitlistResponse {
  success: boolean;
  message: string;
  userId?: string;
  error?: string;
}

export interface FormStep {
  id: string;
  label: string;
  description: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

export interface ExitIntentData {
  isVisible: boolean;
  hasTriggered: boolean;
  lastTriggerTime: number;
}

// API Error Types
export type ApiErrorType = 
  | 'RATE_LIMIT_EXCEEDED'
  | 'INVALID_SESSION'
  | 'DUPLICATE_SUBMISSION'
  | 'VALIDATION_ERROR'
  | 'NETWORK_ERROR'
  | 'UNKNOWN_ERROR';

export interface ApiError {
  type: ApiErrorType;
  message: string;
  details?: any;
}
